import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { allBlocksSelector } from '../Blocks/selectors';
import { currentCourseSelector } from '../Courses/selectors';
import { getModuleForPage } from '../Modules/selectors';
import { selectCurrentInstitution } from '../Institutions/selectors';
import { groupsByPathSelector } from '../Groups/selectors';
import {
  ColumnWidth,
  ColumnGroup,
} from '../../web/containers/BlockPage/consts';
import { allPageUIById } from './simpleSelectors';
import { selectCurrentPagePath } from '../Pages/selectors';

// state selectors
export const favouritesSelector = (state) =>
  state.BlockPageUI.get('favourites');

export const subscriptionsSelector = (state) =>
  state.BlockPageUI.get('subscriptions');

export const traversalLinksSelector = (state) =>
  state.BlockPageUI.get('traversalLinks');

export const selectHasNextPageLink = createSelector(
  [traversalLinksSelector, selectCurrentPagePath],
  (traversalLinks, pagePath) =>
    traversalLinks.getIn([pagePath, 'next', 'hasAccess'], false)
);

export const selectDisplayOptions = (state) =>
  state.BlockPageUI.get('displayOptions');

export const communityOptionsSelector = (state) =>
  state.BlockPageUI.get('communityOptions');

export const userCommunityOptionsSelector = (state) =>
  state.BlockPageUI.get('userCommunityOptions');

export const makeSelectCurrentShareModal = (mountId) => (state) =>
  state.BlockPageUI.getIn(['currentShareModal', mountId], Immutable.Map());

export const makeSelectShareModalCallback = (mountId) => (state) =>
  state.BlockPageUI.getIn(['currentShareModal', mountId, 'onPost']);

// selectors
export const makeGetPageUI = (mountId) =>
  createSelector([allPageUIById], (allPageUI) => allPageUI.getIn([mountId]));

export const makeSelectIsEditor = (mountId) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, 'isEditor'], false)
  );

export const makeSelectIsAdmin = (mountId) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, 'isAdmin'], false)
  );

export const makeSelectPageGroupType = (mountId) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, 'pageGroupType'], null)
  );

export const makeSelectUserCohortGroups = (mountId) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, 'userCohortGroups'], Immutable.Map())
  );

export const makeSelectCourseGroupTypes = (mountId) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, 'courseGroupTypes'], Immutable.List())
  );

export const makeSelectEulaAccepted = (mountId) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, 'eulaAccepted'], true)
  );

export const makeSelectLastUpdatedBlocks = (mountId) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, 'lastUpdatedBlocks'], Immutable.List())
  );

const DEFAULT_SCHEDULED_ACCESS = Immutable.fromJS({
  reasons: [],
});

export const makeSelectScheduledAccess = (mountId) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, 'scheduledAccess'], DEFAULT_SCHEDULED_ACCESS)
  );

export const makeSelectIsSubscribed = (pagePath) =>
  createSelector([subscriptionsSelector], (subscriptions) => {
    const pageSubscriptions = subscriptions.getIn(
      [pagePath, 'actionTypes'],
      null
    );
    return pageSubscriptions ? pageSubscriptions.size > 0 : false;
  });

export const makeSelectIsLiked = (pagePath) =>
  createSelector([favouritesSelector], (pageFavourites) =>
    pageFavourites.getIn([pagePath, 'isFavourited'], false)
  );

export const defaultPageTraversalLinks = Immutable.fromJS({
  back: null,
  next: null,
});

export const makeSelectPageTraversalLinks = (pagePath) =>
  createSelector([traversalLinksSelector], (traversalLinks) => {
    return traversalLinks.get(pagePath, defaultPageTraversalLinks);
  });

export const makeSelectDisplayOptions = (pagePath) =>
  createSelector([selectDisplayOptions], (displayOptions) => {
    return displayOptions.get(pagePath, Immutable.Map());
  });

export const makeSelectPageUIAttribute = (
  mountId,
  attributeName,
  defaultValue
) =>
  createSelector([allPageUIById], (allPageUI) =>
    allPageUI.getIn([mountId, attributeName], defaultValue)
  );

export const makeSelectPageMode = (mountId) =>
  makeSelectPageUIAttribute(mountId, 'mode', 'view');

const defaultTags = null;
export const makeSelectPageTags = (mountId) =>
  makeSelectPageUIAttribute(mountId, 'tags', defaultTags);

const defaultAccessContext = Immutable.fromJS({
  context: 'default',
});

export const makeSelectAccessContext = (mountId) =>
  makeSelectPageUIAttribute(mountId, 'accessContext', defaultAccessContext);

export const makeSelectCurrentGroup = (pageId) =>
  createSelector(
    [userCommunityOptionsSelector],
    (userCommunityOptionsByPageId) =>
      userCommunityOptionsByPageId.getIn([pageId, 'currentGroup'], null)
  );

export const makeSelectCommunityOptionsAttribute = (
  pageId,
  attributeName,
  defaultValue
) =>
  createSelector([communityOptionsSelector], (communityOptionsByPageId) =>
    communityOptionsByPageId.getIn([pageId, attributeName], defaultValue)
  );

export const makeSelectCommentsHeading = (pageId) =>
  makeSelectCommunityOptionsAttribute(pageId, 'commentsHeading', 'Comments');

export const makeSelectIsCommentingDisabled = (pageId) =>
  makeSelectCommunityOptionsAttribute(pageId, 'isCommentingDisabled', true);

// Mapping from communityOptions and userCommunityOptions attributes
// to those expected by OLCommenting
export const makeSelectCommentOptions = (mountId, pageId) =>
  createSelector(
    [allPageUIById, communityOptionsSelector],
    (allPageUI, communityOptionsByPageId) => {
      let options = Immutable.Map();
      if (mountId && allPageUI.hasIn([mountId, 'canPostComments'])) {
        options = options.set(
          'cancomment',
          allPageUI.getIn([mountId, 'canPostComments'])
        );
      }
      if (
        pageId &&
        communityOptionsByPageId.hasIn([pageId, 'commentPlaceholder'])
      ) {
        options = options.set(
          'placeholder',
          communityOptionsByPageId.getIn([pageId, 'commentPlaceholder'])
        );
      }
      return options;
    }
  );

export const makeSelectCommunityOptions = (pageId) =>
  createSelector([communityOptionsSelector], (communityOptionsByPageId) => {
    return communityOptionsByPageId.get(pageId, Immutable.Map());
  });

export const makeSelectCommentPlaceholder = (pageId) =>
  makeSelectCommunityOptionsAttribute(
    pageId,
    'commentPlaceholder',
    'Write a comment ...'
  );

export const lookupPageUIBlocks = (pageUI, allBlocks) => {
  let mountedBlocks = Immutable.List();
  pageUI
    .get('blocks')
    .keySeq()
    .forEach((blockId) => {
      mountedBlocks = mountedBlocks.push(allBlocks.get(`block-${blockId}`));
    });
  return mountedBlocks;
};

export const selectPageUIBlocksById = createSelector(
  [allPageUIById, allBlocksSelector],
  (allPageUI, allBlocks) =>
    allPageUI.map((pageUI) => lookupPageUIBlocks(pageUI, allBlocks))
);

export const makeSelectHasSubmitButton = (mountId) =>
  createSelector([selectPageUIBlocksById], (pageUIBlocksById) => {
    const pageUIBlocks = pageUIBlocksById.get(mountId);
    if (!pageUIBlocks) {
      return false;
    }
    return !!pageUIBlocks.find((block) => block && block.get('isSubmitButton'));
  });

/*
 * Reads block information from list of blocks and returns information on how
 * to render the blockpage layout in a sane format.
 */
export const blocksToBlockLayout = (blocks) => {
  let currentRow = 0; // which row we're on
  let rowData = null;
  const rows = [];

  if (!blocks) {
    return Immutable.List();
  }

  // NOTE: we filter blocks for the situation where the user has access to the
  // page data (which includes block indices), but does not have access to the
  // blocks (via scheduled access checks). Ideally the user would not have
  // scheduled access to the page data, but for now this avoids the layout error
  // for long enough to show the "you do not have access" message.
  blocks.filter(Boolean).forEach((block, index) => {
    const colSize = block.getIn(['setupData', 'colSize'], ColumnWidth.FULL);
    const group = block.getIn(['setupData', 'group'], ColumnGroup.LEFT);
    const row = block.getIn(['setupData', 'row']);

    const blockRowMapping = { id: block.get('id'), index };

    // detected first/a new row
    if (!rowData || currentRow !== row) {
      currentRow = row;

      rowData = {
        row: currentRow,
        totalBlocks: 0,
        isTwoColumn: false,
        hasLeft: false,
        hasRight: false,
        blocks: [],
        blocksLeft: [],
        blocksRight: [],
      };

      rows.push(rowData);
    }

    const isHalfWidth = colSize === ColumnWidth.HALF;

    if (!isHalfWidth) {
      // full-width block
      rowData.blocks.push(blockRowMapping);
    } else {
      // half-width block
      rowData.isTwoColumn = rowData.isTwoColumn || isHalfWidth;

      const isRightColumn = group === ColumnGroup.RIGHT;
      rowData.hasLeft = rowData.hasLeft || !isRightColumn;
      rowData.hasRight = rowData.hasRight || isRightColumn;

      const blocksList = isRightColumn ? 'blocksRight' : 'blocksLeft';
      rowData[blocksList].push(blockRowMapping);
    }
    rowData.totalBlocks += 1;
  });

  return Immutable.fromJS(rows);
};

export const makeSelectBreadcrumbs = (pageId, mountId) =>
  createSelector(
    [
      makeGetPageUI(mountId),
      selectCurrentInstitution,
      currentCourseSelector,
      getModuleForPage,
      groupsByPathSelector,
    ],
    (pageUI, currentInstitution, currentCourse, modulesByPage, groups) => {
      const crumbs = [];
      const module = modulesByPage.get(pageId);

      // Pages in modules return module information
      // `Module set > module name`
      if (module) {
        if (module.get('setName')) {
          crumbs.push(module.get('setName'));
        }

        crumbs.push(module.get('title'));
        return Immutable.fromJS(crumbs);
      }

      // Otherwise fall back to what we know
      // (Institution /) 'Courses' / Course name
      if (currentInstitution) {
        crumbs.push(currentInstitution.get('name'));
      }

      if (currentCourse) {
        crumbs.push('Courses');
        crumbs.push(currentCourse.get('name'));
      }

      // Extra group information if in a group context
      if (pageUI) {
        const groupPath = pageUI.getIn(['pathContext', 'groupPath']);
        const superPage = pageUI.getIn(['pathContext', 'superPage']);
        if (groupPath && groups.get(groupPath)) {
          const group = groups.get(groupPath);
          crumbs.push('Groups');
          crumbs.push(group.get('name'));
        } else if (superPage) {
          crumbs.push(superPage);
        }
      }

      return Immutable.fromJS(crumbs);
    }
  );
